import * as React from 'react';
import { forEach } from 'lodash-es';
import { FormInterface } from 'Interfaces/Forms/FormsInterface';
import { QuestionInterface, questionTypes } from 'Interfaces/Forms/QuestionInterface';
import { globalWindow } from 'global/global';
import { Elements } from '../elements';
import { DataPoint } from '../../../Interfaces/DataPoint';
import { ClientPersistInterface } from '../../../Interfaces/ClientPersistInterface';
import MapContainer from '../../Map/MapContainer';
import FormUtils from './FormUtils';
import { isTaskQuestionVisible } from './TaskUtils';
import { canEdit, canView } from './RolesChecker';
import { getDiagram, getSubformDiagrams } from './DiagramUtils';
import { getSubformMainDiagramValues } from './utils';

/*
  This function returns the table element for rendering.
  places here to reduce cyclomatic complexity just incase you are wondering if the dev
  who wrote it was nuts or high on weed or some other lethal substances.
*/
export const getTableElement = (
  question: QuestionInterface, dataPoint: DataPoint, forms: FormInterface[], updateAnswer,
  formUtils: FormUtils, clientPersist: ClientPersistInterface, parentDataPoint?: DataPoint,
  parentModel?: FormInterface, disabled?: boolean, isSubQuestion?: boolean, newAnswer?: boolean
): JSX.Element | null => {
  const tableForm = forms.find( f => f.ref === question.table?.columns.relation[0].ref);
  const subForm = question.type === questionTypes.REFERENCE_TABLE_QUESTION && question.value ?
    forms.find(f => f.ref === question.value) : undefined;
  return tableForm ? (
    <Elements.TableQuestion
      question={question}
      dataPoint={dataPoint}
      tableForm={tableForm}
      subForm={subForm}
      key={question.id}
      updateAnswer={updateAnswer}
      formUtils={formUtils}
      forms={forms}
      parentModel={parentModel}
      parentDataPoint={parentDataPoint}
      disabled={disabled}
      isSubQuestion={isSubQuestion}
      newAnswer={newAnswer}
    />
  ) : null;
};

export const getSubFormElement = (
  question: QuestionInterface, dataPoint: DataPoint, forms: FormInterface[], updateAnswer,
  formUtils: FormUtils, clientPersist: ClientPersistInterface, parentModel?: FormInterface,
  disabled?: boolean, isSubQuestion?: boolean, newAnswer?: boolean
): JSX.Element | null => {
  return (
    <Elements.SubFormQuestion
      question={question}
      dataPoint={dataPoint}
      updateAnswer={updateAnswer}
      forms={forms}
      formUtils={formUtils}
      key={question.id}
      parentModel={formUtils.getModel()}
      clientPersist={clientPersist}
      edit={canEdit(question, clientPersist, disabled, newAnswer)}
      isSubQuestion={isSubQuestion}
    />
  );
};
/*
  Returns the question element for the given question object.
*/
/* eslint-disable complexity */
export const getQuestionElement = (
  question: QuestionInterface, dataPoint: DataPoint, isTable: boolean, forms: FormInterface[], updateAnswer,
  formUtils: FormUtils, newAnswer: boolean, clientPersist: ClientPersistInterface, parentModel?: FormInterface,
  parentDataPoint?: DataPoint, parentQuestion?: QuestionInterface, isSubQuestion?: boolean, disabled?: boolean
): JSX.Element | null => {
  switch (question.type) {
    case questionTypes.INT_QUESTION:
    case questionTypes.FLOAT_QUESTION:
      return (
        <Elements.IntQuestion
          question={question}
          dataPoint={dataPoint}
          edit={canEdit(question, clientPersist, disabled, newAnswer)}
          key={question.id}
          updateAnswer={updateAnswer}
          formUtils={formUtils}
          isSubQuestion={isSubQuestion}
        />
      );
    case questionTypes.TIME_QUESTION:
      return (
        <Elements.TimeQuestion
          key={question.id}
          question={question}
          value={dataPoint[question.id]}
          dataPoint={dataPoint}
          updateAnswer={updateAnswer}
          formUtils={formUtils}
          edit={canEdit(question, clientPersist, disabled, newAnswer)}
          isSubQuestion={isSubQuestion}
        />
      );
    case questionTypes.DATE_QUESTION:
      return (
        <Elements.DateQuestion
          dataPoint={dataPoint}
          question={question}
          value={dataPoint[question.id]}
          key={question.id}
          updateAnswer={updateAnswer}
          formUtils={formUtils}
          edit={canEdit(question, clientPersist, disabled, newAnswer)}
          isSubQuestion={isSubQuestion}
          clientPersist={clientPersist}
        />
      );
    case questionTypes.SELECT_ONE_QUESTION:
    case questionTypes.SELECT_MULTIPLE_QUESTION:
    case questionTypes.BOOLEAN_QUESTION:
    case questionTypes.LIKERT_SCALE_QUESTION:
    case questionTypes.STATUS_QUESTION:
      return (
        <Elements.SelectQuestion
          question={question}
          dataPoint={dataPoint}
          key={question.id}
          isTable={isTable}
          updateAnswer={updateAnswer}
          formUtils={formUtils}
          forms={forms}
          newAnswer={newAnswer}
          parentModel={parentModel}
          parentDataPoint={parentDataPoint}
          isSubQuestion={isSubQuestion}
          clientPersist={clientPersist}
          edit={canEdit(question, clientPersist, disabled, newAnswer)}
        />
      );
    case questionTypes.DIGITAL_SIGNATURE_QUESTION:
      return (
        <Elements.DigitalSignatureQuestion
          question={question}
          dataPoint={dataPoint}
          key={question.id}
          isTable={isTable}
          updateAnswer={updateAnswer}
          formUtils={formUtils}
          forms={forms}
          newAnswer={newAnswer}
          parentModel={parentModel}
          parentDataPoint={parentDataPoint}
          isSubQuestion={isSubQuestion}
          clientPersist={clientPersist}
          edit={canEdit(question, clientPersist, disabled, newAnswer)}
        />
      );
    case questionTypes.RANK_ORDER_QUESTION:
      return (
        <Elements.RankOrderQuestion
          question={question}
          dataPoint={dataPoint}
          key={question.id}
          isTable={isTable}
          updateAnswer={updateAnswer}
          formUtils={formUtils}
          forms={forms}
          newAnswer={newAnswer}
          parentModel={parentModel}
          parentDataPoint={parentDataPoint}
          isSubQuestion={isSubQuestion}
          clientPersist={clientPersist}
          edit={canEdit(question, clientPersist, disabled, newAnswer)}
        />
      );
    case questionTypes.SELECT_USER_QUESTION:
    case questionTypes.USER_QUESTION:
      return (
        <Elements.UserQuestion
          question={question}
          dataPoint={dataPoint}
          key={question.id}
          updateAnswer={updateAnswer}
          formUtils={formUtils}
          edit={disabled ? false : canEdit(question, clientPersist, disabled, newAnswer)}
          isSubQuestion={isSubQuestion}
        />
      );
    case questionTypes.TABLE_QUESTION:
    case questionTypes.REFERENCE_TABLE_QUESTION:
      return getTableElement(
        question,
        dataPoint,
        forms,
        updateAnswer,
        formUtils,
        clientPersist,
        parentDataPoint,
        parentModel,
        disabled,
        isSubQuestion
      );
    case questionTypes.SKIP_QUESTION:
      return (
        <Elements.SkipQuestion
          question={question}
          dataPoint={dataPoint}
          key={question.id}
          updateAnswer={updateAnswer}
          formUtils={formUtils}
          forms={forms}
          newAnswer={newAnswer}
          parentModel={parentModel}
          parentQuestion={parentQuestion}
          parentDataPoint={parentDataPoint}
          isSubQuestion={isSubQuestion}
          clientPersist={clientPersist}
          edit={canEdit(question, clientPersist, disabled, newAnswer)}
        />
      );
    case questionTypes.LOOKUP_VALUES_QUESTION:
      return (
        <Elements.LookupValuesQuestion
          question={question}
          dataPoint={dataPoint}
          key={question.id}
          updateAnswer={updateAnswer}
          formUtils={formUtils}
          parentQuestion={parentQuestion}
          forms={forms}
          isSubQuestion={isSubQuestion}
          parentDataPoint={parentDataPoint}
          parentModel={parentModel}
          edit={canEdit(question, clientPersist, disabled, newAnswer)}
        />
      );
    case questionTypes.PICTURES_QUESTION:
    case questionTypes.FILE_QUESTION:
      return (
        <Elements.FileQuestion
          question={question}
          dataPoint={dataPoint}
          key={question.id}
          updateAnswer={updateAnswer}
          formUtils={formUtils}
          accept={question.accept}
          isSubQuestion={isSubQuestion}
          edit={disabled ? false : canEdit(question, clientPersist, disabled, newAnswer)}
        />
      );
    case questionTypes.GPS_QUESTION:
      if (formUtils.showCoordinates()) {
        return (
          <Elements.GPSQuestion
            question={question}
            dataPoint={dataPoint}
            key={question.id}
            updateAnswer={updateAnswer}
            formUtils={formUtils}
            parentDataPoint={parentDataPoint}
            forms={forms}
            edit={disabled ? false : canEdit(question, clientPersist, disabled, newAnswer)}
          />
        );
      }
      break;
    case questionTypes.LABEL:
      return (
        <Elements.LabelQuestion
          question={question}
          dataPoint={dataPoint}
          updateAnswer={updateAnswer}
          key={question.id}
          formUtils={formUtils}
          isSubQuestion={isSubQuestion}
          edit={canEdit(question, clientPersist, disabled)}
          isTable={isTable}
        />
      );
    case questionTypes.PART_OF_QUESTION:
    case questionTypes.TASK_QUESTION:
      return getSubFormElement(
        question, dataPoint, forms, updateAnswer, formUtils, clientPersist,
        parentModel, disabled, isSubQuestion, newAnswer
      );
    case questionTypes.STRING_QUESTION:
      if (question.id === 'address') {
        return (
          <Elements.AddressQuestion
            question={question}
            dataPoint={dataPoint}
            key={question.id}
            updateAnswer={updateAnswer}
            edit={canEdit(question, clientPersist, disabled, newAnswer)}
            formUtils={formUtils}
            isSubQuestion={isSubQuestion}
          />
        );
      }
      return (
        <Elements.StringQuestion
          question={question}
          dataPoint={dataPoint}
          key={question.id}
          updateAnswer={updateAnswer}
          edit={canEdit(question, clientPersist, disabled, newAnswer)}
          formUtils={formUtils}
          isSubQuestion={isSubQuestion}
          parentQuestion={parentQuestion}
          parentModel={parentModel}
          parentDataPoint={parentDataPoint}
          isTable={isTable}
        />
      );
    case questionTypes.NAME_QUESTION:
      if (question.script && question.script.length > 0) {
        return (
          <Elements.CalculatedValueQuestion
            question={question}
            key={question.id}
            updateAnswer={updateAnswer}
            dataPoint={dataPoint}
            formUtils={formUtils}
            forms={forms}
            parentQuestion={parentQuestion}
            isSubQuestion={isSubQuestion}
            parentModel={parentModel}
            parentDataPoint={parentDataPoint}
            isTable={isTable}
            edit={canEdit(question, clientPersist, disabled, newAnswer)}
          />
        );
      }
      return (
        <Elements.StringQuestion
          question={question}
          dataPoint={dataPoint}
          key={question.id}
          updateAnswer={updateAnswer}
          edit={canEdit(question, clientPersist, disabled, newAnswer)}
          formUtils={formUtils}
          isSubQuestion={isSubQuestion}
          parentQuestion={parentQuestion}
          parentModel={parentModel}
        />
      );
    case questionTypes.CALCULATED_VALUE_QUESTION:
      return (
        <Elements.CalculatedValueQuestion
          question={question}
          key={question.id}
          updateAnswer={updateAnswer}
          dataPoint={dataPoint}
          formUtils={formUtils}
          forms={forms}
          parentModel={parentModel}
          parentDataPoint={parentDataPoint}
          parentQuestion={parentQuestion}
          isSubQuestion={isSubQuestion}
          isTable={isTable}
          edit={canEdit(question, clientPersist, disabled, newAnswer)}
        />
      );
    case questionTypes.PHONE_NUMBER_QUESTION:
      return (
        <Elements.StringQuestion
          question={question}
          dataPoint={dataPoint}
          key={question.id}
          updateAnswer={updateAnswer}
          edit={canEdit(question, clientPersist, disabled, newAnswer)}
          formUtils={formUtils}
          isSubQuestion={isSubQuestion}
          type="tel"
        />
      );
    case questionTypes.EMAIL_QUESTION:
      return (
        <Elements.StringQuestion
          question={question}
          dataPoint={dataPoint}
          key={question.id}
          updateAnswer={updateAnswer}
          edit={canEdit(question, clientPersist, disabled, newAnswer)}
          formUtils={formUtils}
          isSubQuestion={isSubQuestion}
          type="email"
        />
      );
    case questionTypes.URL_QUESTION:
      return (
        <Elements.StringQuestion
          question={question}
          dataPoint={dataPoint}
          key={question.id}
          updateAnswer={updateAnswer}
          edit={canEdit(question, clientPersist, disabled, newAnswer)}
          formUtils={formUtils}
          isSubQuestion={isSubQuestion}
          type="url"
        />
      );
    case questionTypes.LAYOUT_TABLE_QUESTION:
      return (
        <Elements.LayoutTableQuestion
          question={question}
          dataPoint={dataPoint}
          formUtils={formUtils}
          updateAnswer={updateAnswer}
          forms={forms}
          clientPersist={clientPersist}
        />
      );
    case questionTypes.VALIDATION_RULE_QUESTION:
      return (
        <Elements.ValidationRuleQuestion
          question={question}
          dataPoint={dataPoint}
          key={question.id}
          updateAnswer={updateAnswer}
          formUtils={formUtils}
          forms={forms}
          newAnswer={newAnswer}
          parentModel={parentModel}
          parentQuestion={parentQuestion}
          parentDataPoint={parentDataPoint}
          isSubQuestion={isSubQuestion}
          clientPersist={clientPersist}
        />
      );
  }
  return null;
};
/* eslint-enable complexity */

export const isShown = (
  question: QuestionInterface, formUtils: FormUtils, newAnswer: boolean, dataPoint: DataPoint,
  clientPersist: ClientPersistInterface
): boolean => {
  if (calculatedSkipHideSingleInstance(question)) {
    return true;
  }
  if (!canView(question, clientPersist)) {
    if (question.type === questionTypes.CALCULATED_VALUE_QUESTION) {
      return true;
    }
    return false;
  }
  if (!question.inVisible && !question.deleted && !question.hideInSingleInstance) {
    if ((!isTaskQuestionVisible(question, dataPoint))
      || question.id === 'parentRowId' || question.id === 'subformID' || question.id === 'parentId') {
      return false;
    } else if (!formUtils.getModel().isChild && question.id === 'BackgroundDiagram') {
      if (!newAnswer || formUtils.getModel().addToDiagram) {
        return false;
      }
    }/* else if (newAnswer && question.type === 'DigitalSignatureQuestion') {
      return false;
    }*/ else if (question.id === 'BackgroundDiagram' && formUtils.getModel().addToDiagram) {
      return false;
    } else if (!newAnswer && ['scheduleDate', 'scheduleStatus'].indexOf(question.id) > -1) {
      return false;
    }
    return true;
  }
  return false;
};

export const calculatedSkipHideSingleInstance = (question) => {
  // Calculated & Skip questions should be rendered at least in virtual dom.
  if (question.hideInSingleInstance &&
    (question.type === 'CalculatedValueQuestion' ||
    question.type === 'SkipQuestion' ||
    question.type === 'ReferenceTableQuestion' ||
    question.type === questionTypes.LOOKUP_VALUES_QUESTION ||
    (question.type === 'NameQuestion' && question.script && question.script.length > 0))
  ) {
    return true;
  }
  return false;
};

export const getTableColumn = (question, el: JSX.Element, id) => {
  if (calculatedSkipHideSingleInstance(question)) {
    return el;
  }
  return (<td key={`table_${question.id}_${id}`}>{el}</td>);
};

export const canViewLocationHierarchy = (form: FormInterface, clientPersist: ClientPersistInterface) => {
  const { roles } = clientPersist;
  if (roles.includes('admin') || roles.includes('modeler')) {
    return true;
  }
  if (form.viewLocations && form.viewLocations.includes(roles)) {
    return true;
  }
  return false;
};

export const canEditLocationHierarchy = (form: FormInterface, clientPersist: ClientPersistInterface) => {
  const { roles } = clientPersist;
  if (roles.includes('admin') || roles.includes('modeler')) {
    return true;
  }
  if (form.editLocations && form.editLocations.includes(roles)) {
    return true;
  }
  return false;
};

/*
This function returns the image/video attached to this question.
*/

export const getMediaView = (question: QuestionInterface): JSX.Element[] | null  => {
  if (question.mediaId && question.mimeType) {
    const cred = `&userid=${globalWindow.userName}&key=${globalWindow.pwrd}`;
    const ids = question.mediaId.split(',');
    const mimeTypes = question.mimeType.split(',');
    const mediaViews: JSX.Element[] = ids.map((id, index) => {
      if (mimeTypes[index].indexOf('image') !== -1) {
        return (
          <div className="row col-md-12" key={`${question.id}-image-div`}>
            <img src={`/json/atool/app/getMedia?action=optionMedia&id=${id}${cred}`} alt="" className="option-media"/>
          </div>
        );
      } else if (mimeTypes[index].indexOf('video') !== -1) {
        return (
          <div className="row col-md-12" key={`${question.id}-video-div`}>
            <video
              controls
              src={`/json/atool/app/getMedia?action=optionMedia&id=${id}${cred}`}
              className="option-media"
            />
          </div>
        );
      } else if (mimeTypes[index].indexOf('audio') !== -1) {
        return (
          <div className="row col-md-12" key={`${question.id}-audio-div`}>
            <audio
              controls
              src={`/json/atool/app/getMedia?action=optionMedia&id=${id}${cred}`} className="option-media"
            >
              Your browser does not support the <code>audio</code> element.
            </audio>
          </div>
        );
      }
      return null;
    });
    return mediaViews;
  }
  return null;
};

/*
  Creates an array of question elemsnts for the given question array.
*/
export const renderQuestions = (
  questions: QuestionInterface[], dataPoint: DataPoint, isTable: boolean, forms: FormInterface[], updateAnswer,
  formUtils: FormUtils, newAnswer: boolean, clientPersist: ClientPersistInterface, parentModel?: FormInterface,
  parentDataPoint?: DataPoint, parentQuestion?: QuestionInterface, isSubQuestion?: boolean, disabled?: boolean
): JSX.Element[] => {
  const elements: JSX.Element[] = [];
  for (const question of questions) {
    if (!question) {
      elements.push(isTable ? (<td key={`table_empty_cell`}/>) : <div/>);
      continue;
    }
    if (question.id === 'subfrmfld' && parentQuestion) {
      elements.push(
        (<Elements.SubFormField key={`${parentQuestion.id}`} question={question} parentQuestion={parentQuestion}/>));
    } else if (isShown(question, formUtils, newAnswer, dataPoint, clientPersist)) {
      const el = getQuestionElement(
        question, dataPoint, isTable, forms, updateAnswer, formUtils, newAnswer, clientPersist,
        parentModel, parentDataPoint, parentQuestion, isSubQuestion, disabled
      );
      if (el) {
        elements.push(isTable ? getTableColumn(question, el, dataPoint['id']) : el);
      }
    }

    if (question.type === questionTypes.GPS_QUESTION && question.inVisible && formUtils.getModel().recordGPSTrace) {
      elements.push(
        <div className="container-fluid map">
          <MapContainer
            id={formUtils.getModel().ref}
            dataPoint={dataPoint}
          />
        </div>
      );
    }
    if (question.type === questionTypes.NAME_QUESTION) {
      if (formUtils.showLocationHierarchy() && canViewLocationHierarchy(formUtils.getModel(), clientPersist)) {
        elements.push((
          <Elements.LocationHierarchyComponent
            dataPoint={dataPoint}
            key={'location-hierarchy'}
            updateAnswer={updateAnswer}
            formUtils={formUtils}
            edit={!disabled}
          />
        ));
      }
      let mainFormDiagramName;
      if (formUtils.getModel().backgroundDiagram && !formUtils.getModel().isChild) {
        mainFormDiagramName = getDiagram(dataPoint['files']);
        if (mainFormDiagramName) {
          elements.push((
            <div className="col-md-12 map">
              <MapContainer
                id={`diagram_map`}
                dataPoint={dataPoint}
                diagramName={mainFormDiagramName}
                propsDataPoints={getSubformMainDiagramValues(dataPoint, formUtils, mainFormDiagramName)}
              />
            </div>
          ));
        }
      }
      // if diagram is same as main form no need to show it differently.
      const diagrams = getSubformDiagrams(dataPoint, formUtils);
      if (mainFormDiagramName && diagrams[mainFormDiagramName]) {
        delete diagrams[mainFormDiagramName];
      }
      forEach(diagrams, (value, key) => {
        elements.push((
          <div className="col-md-12 map diagram_map" key={`diagram_map_${key}`}>
            <MapContainer
              id={`diagram_map_${key}`}
              dataPoint={dataPoint}
              diagramName={key}
              propsDataPoints={value}
            />
          </div>
        ));
      });
    }
  }
  return elements;
};

export const getQuestions = (
  questions: QuestionInterface[],
  dataPoint: DataPoint,
  formUtils: FormUtils,
  newAnswer: boolean,
  clientPersist: ClientPersistInterface
) => {
  return questions
    ? questions.filter((q) =>
      isShown(q, formUtils, newAnswer, dataPoint, clientPersist),
    )
    : [];
};
